import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@luparx/i18n';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardStack,
  Checkbox,
  EmptyState,
  FormField,
  IconCar,
  IconMore,
  IconPlus,
  Input,
  Modal,
  Select,
} from '@luparx/ui';
import { VehicleImage } from '../vehicleImages/VehicleImage';
import {
  guardarCarroceriaElegida,
  leerCarroceriaElegida,
  olvidarCarroceriaElegida,
  useCarroceriaElegida,
} from '../vehicleImages/carroceriaElegida';
import { categoriaVisible } from '../vehicleImages/categoriaVisible';
import {
  CARROCERIAS_OFRECIDAS,
  ETIQUETA_DE_CARROCERIA,
} from '../vehicleImages/prototipo/catalogoPrototipo';
import type { CreateVehicleRequest, Vehicle } from '@luparx/api-client';
import { CitizenShell } from '../components/CitizenShell';
import { QueryBoundary } from '../components/QueryBoundary';
import { vehicleDeleteErrorMessage, vehiclePlateError, vehicleSaveErrorMessage } from '../lib/apiErrors';
import { normalizePlate } from '../lib/plate';
import { catalogLabeller, vehicleDescriptor } from '../lib/vehiclePresentation';
import {
  useActiveParkingSessions,
  useCreateVehicle,
  useDeleteVehicle,
  useSetPrimaryVehicle,
  useUpdateVehicle,
  useVehicleColorCatalog,
  useVehicleTypeCatalog,
  useVehicles,
} from '../lib/queries';

interface VehicleFormState {
  plate: string;
  name: string;
  brand: string;
  model: string;
  year: string;
  /** Catalog key. Never a literal in this file — see `DEFAULT_VEHICLE_TYPE`. */
  type: string;
  /**
   * La carrocería elegida, o `''` para «que la elija la aplicación».
   *
   * <p>No viaja al servidor: el contrato no tiene dónde ponerla —ver `carroceriaElegida.ts`— y se
   * guarda en este dispositivo después de que el servidor confirme el vehículo.</p>
   */
  carroceria: string;
  color: string;
  isOwner: boolean;
}

/**
 * Una sola pregunta para la persona, dos destinos por debajo.
 *
 * <p>El valor del selector es `TIPO:carroceria`: `CAR:pickup`, `MOTORCYCLE:`, `CAR:` para «no lo
 * sé». Lo de antes de los dos puntos es del servidor y viaja en el `type` del DTO; lo de después
 * se queda en el dispositivo. Partirlo en dos selectores sería honesto con la arquitectura y
 * malísimo para quien registra un carro: nadie quiere contestar «automóvil» y después «pick-up».</p>
 */
function valorDeSelector(form: Pick<VehicleFormState, 'type' | 'carroceria'>): string {
  return `${form.type}:${form.carroceria}`;
}

function desdeValorDeSelector(valor: string): Pick<VehicleFormState, 'type' | 'carroceria'> {
  const corte = valor.indexOf(':');
  if (corte < 0) return { type: valor, carroceria: '' };
  return { type: valor.slice(0, corte), carroceria: valor.slice(corte + 1) };
}

/**
 * The type the server itself defaults to when a client omits it. Written down once, here, because
 * a form has to preselect *something* before the catalog answers; the moment the catalog arrives,
 * this is only used if it actually lists this key.
 */
const DEFAULT_VEHICLE_TYPE = 'CAR';

const EMPTY_FORM: VehicleFormState = {
  plate: '',
  name: '',
  brand: '',
  model: '',
  year: '',
  type: DEFAULT_VEHICLE_TYPE,
  carroceria: '',
  color: '',
  isOwner: false,
};

function formFromVehicle(vehicle: Vehicle): VehicleFormState {
  return {
    plate: vehicle.plate,
    name: vehicle.name ?? '',
    brand: vehicle.brand ?? '',
    model: vehicle.model ?? '',
    year: vehicle.year ? String(vehicle.year) : '',
    type: vehicle.type || DEFAULT_VEHICLE_TYPE,
    // Un vehículo registrado antes de que existiera esta pregunta no tiene nada guardado, y eso es
    // «que la elija la aplicación»: abre el formulario sin elección y se sigue editando igual.
    carroceria: leerCarroceriaElegida(vehicle.id) ?? '',
    color: vehicle.color ?? '',
    isOwner: vehicle.isOwner,
  };
}

function toCreateRequest(form: VehicleFormState): CreateVehicleRequest {
  return {
    plate: normalizePlate(form.plate),
    name: form.name.trim() || undefined,
    brand: form.brand.trim() || undefined,
    model: form.model.trim() || undefined,
    year: form.year.trim() ? Number(form.year) : undefined,
    type: form.type,
    // An empty pick means "did not say", which is a value the server accepts — not the string "".
    color: form.color || undefined,
    isOwner: form.isOwner,
  };
}

/**
 * Real CRUD against `/api/v1/citizen/vehicles` (CONTRACT.md v0.2). Only `plate` is required; the
 * server enforces per-user plate uniqueness, surfaced here next to the field (never as a global
 * error) — and rejects deleting a vehicle with an active session, surfaced in the delete dialog.
 */
export function VehiclesPage(): React.JSX.Element {
  const { t } = useTranslation();
  const tKey = (key: string): string => t(key as Parameters<typeof t>[0]);
  const vehiclesQuery = useVehicles();
  const typeCatalogQuery = useVehicleTypeCatalog();
  const colorCatalogQuery = useVehicleColorCatalog();
  const typeOf = catalogLabeller(typeCatalogQuery.data, tKey);
  const colorOf = catalogLabeller(colorCatalogQuery.data, tKey);

  /**
   * Las opciones del selector: el catálogo del servidor, con el automóvil desplegado en carrocerías.
   *
   * <p>La lista de TIPOS sigue viniendo del servidor y no está escrita acá —si la plataforma
   * agrega uno mañana, aparece sin publicar esta aplicación, que es como estaba hecho y no se
   * rompe—. Lo que se añade es el despliegue del automóvil, y sólo del automóvil: es el único tipo
   * donde el contrato no distingue, porque `V16_0` dejó a la pick-up y al sedán siendo los dos
   * `CAR`. Un tipo nuevo del servidor entra como una opción suya, sin carrocerías, hasta que
   * alguien decida si las necesita.</p>
   */
  function opcionesDeTipo(
    types: readonly { value: string; labelKey: string }[],
  ): { value: string; label: string }[] {
    return types.flatMap((entry) => {
      if (entry.value !== DEFAULT_VEHICLE_TYPE) {
        return [{ value: `${entry.value}:`, label: tKey(entry.labelKey) }];
      }
      return [
        /*
          La opción de no elegir encabeza la lista y es lo que trae un formulario nuevo: quien no
          sepa o no quiera elegir carrocería deja esto y la aplicación la deduce del modelo.

          Desde el 23-10-2026 NO dice «Automóvil» a secas, dice «Automóvil (la elige la
          aplicación)». Antes el rótulo del servidor alcanzaba porque era también lo que la tarjeta
          mostraba; ahora la tarjeta de un Hilux dice «Pick-up», y abrir el formulario y leer
          «Automóvil» dejaba a la persona creyendo que algo no se había guardado. Lo que esta
          opción significa no es «es un automóvil genérico»: es «decidila vos por mí».
        */
        { value: `${entry.value}:`, label: t('citizen.vehicles.form.typeAutoChosen') },
        ...CARROCERIAS_OFRECIDAS.map((clave) => ({
          value: `${entry.value}:${clave}`,
          label: tKey(ETIQUETA_DE_CARROCERIA[clave] ?? clave),
        })),
      ];
    });
  }
  const { data: activeSessions } = useActiveParkingSessions();
  const createVehicle = useCreateVehicle();
  const updateVehicle = useUpdateVehicle();
  const setPrimaryVehicle = useSetPrimaryVehicle();
  const deleteVehicle = useDeleteVehicle();

  // undefined = form closed · null = add mode · Vehicle = edit mode
  const [formVehicle, setFormVehicle] = useState<Vehicle | null | undefined>(undefined);
  const [form, setForm] = useState<VehicleFormState>(EMPTY_FORM);
  const [plateError, setPlateError] = useState<string | undefined>(undefined);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [menuVehicle, setMenuVehicle] = useState<Vehicle | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vehicle | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Only stays on the citizen's own vehicles mark a row "En uso". A stay on a borrowed plate has no
  // vehicle behind it (CONTRACT.md v0.11), and letting a null in here would mark nothing — or, if a
  // vehicle id were ever null-ish, the wrong row.
  const activeVehicleIds = useMemo(
    () => new Set((activeSessions ?? []).map((session) => session.vehicleId).filter((id): id is string => id !== null)),
    [activeSessions],
  );

  /**
   * A new vehicle starts as a car because that is what the server defaults to — but only if this
   * platform still calls it `CAR`. Once the catalog has answered, an add form holding a key the
   * catalog does not list falls back to the first type it does, so the form can never submit a
   * type this deployment would reject. An *edit* form is left alone: the value came from the
   * server and belongs to the record, whatever the catalog currently lists.
   */
  useEffect(() => {
    const types = typeCatalogQuery.data;
    if (!types || types.length === 0 || formVehicle !== null) return;
    if (types.some((entry) => entry.value === form.type)) return;
    setForm((f) => ({ ...f, type: types[0]!.value }));
  }, [typeCatalogQuery.data, formVehicle, form.type]);

  function openAdd(): void {
    setForm(EMPTY_FORM);
    setPlateError(undefined);
    setSaveError(null);
    setFormVehicle(null);
  }

  function openEdit(vehicle: Vehicle): void {
    setForm(formFromVehicle(vehicle));
    setPlateError(undefined);
    setSaveError(null);
    setMenuVehicle(null);
    setFormVehicle(vehicle);
  }

  function closeForm(): void {
    setFormVehicle(undefined);
  }

  async function handleSubmit(): Promise<void> {
    setPlateError(undefined);
    setSaveError(null);
    const payload = toCreateRequest(form);
    try {
      /*
        La carrocería se guarda DESPUÉS de que el servidor confirme, y con el identificador que él
        devuelve. Guardarla antes dejaría una carrocería huérfana cada vez que una placa repetida
        hace fallar el alta: el mapa del dispositivo se llenaría de elecciones de vehículos que no
        existen, y la primera que coincidiera con un identificador ajeno pintaría mal otra tarjeta.
      */
      if (formVehicle) {
        await updateVehicle.mutateAsync({ id: formVehicle.id, payload });
        guardarCarroceriaElegida(formVehicle.id, form.carroceria || null);
      } else {
        const creado = await createVehicle.mutateAsync(payload);
        if (creado?.id) guardarCarroceriaElegida(creado.id, form.carroceria || null);
      }
      closeForm();
    } catch (error) {
      // A rejected plate belongs next to the plate field; anything else is not about that input
      // and must not be reported as if it were — it goes above the form, carrying the server's
      // stable code and traceId so a report can be looked up instead of reproduced.
      const plateMessage = vehiclePlateError(error, t);
      if (plateMessage) setPlateError(plateMessage);
      else setSaveError(vehicleSaveErrorMessage(error, t));
    }
  }

  async function handleSetPrimary(vehicle: Vehicle): Promise<void> {
    setMenuVehicle(null);
    await setPrimaryVehicle.mutateAsync(vehicle.id);
  }

  async function handleDelete(): Promise<void> {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteVehicle.mutateAsync(deleteTarget.id);
      // Y se olvida su carrocería: sin esto el mapa del dispositivo crece para siempre y, el día
      // que el servidor reutilizara un identificador, el vehículo nuevo heredaría la del borrado.
      olvidarCarroceriaElegida(deleteTarget.id);
      setDeleteTarget(null);
    } catch (error) {
      setDeleteError(vehicleDeleteErrorMessage(error, t));
    }
  }

  const isSaving = createVehicle.isPending || updateVehicle.isPending;

  return (
    <CitizenShell
      heading={
        <div className="lx-page-header">
          {/* El titulo se queda en una linea: partido en dos ("Mis / vehiculos") se lee como un error. */}
          <h1 className="lx-text-screen-title lx-page-header__title">{t('citizen.vehicles.title')}</h1>
          <Button type="button" variant="solid" onClick={openAdd}>
            <IconPlus size={16} /> {t('citizen.vehicles.addCta')}
          </Button>
        </div>
      }
    >
      <QueryBoundary
        query={vehiclesQuery}
        errorTitle={t('citizen.vehicles.title')}
        isEmpty={(list) => list.length === 0}
        empty={
          <Card>
            <EmptyState
              icon={<IconCar size={28} />}
              title={t('citizen.vehicles.empty.title')}
              description={t('citizen.vehicles.empty.description')}
            />
          </Card>
        }
      >
        {(vehicles) => (
        <CardStack>
          {/*
            La advertencia, UNA vez y arriba de todo.

            La imagen de cada tarjeta es una ilustración genérica elegida por tipo, modelo y color;
            no es una foto del vehículo de esa persona y no afirma su carrocería. Decirlo acá y no
            en cada tarjeta es lo que evita repetir cuatro veces la misma frase en una lista de
            cuatro vehículos, y deja la tarjeta para los datos.
          */}
          <p className="lx-text-meta lx-vehiculos-aviso">{t('citizen.vehicles.imageIsGeneric')}</p>
          {vehicles.map((vehicle) => (
            /*
              La tarjeta compacta (22-10-2026). Tres columnas: imagen, datos, menú.

              Antes era imagen+placa arriba, descripción debajo y una fila de hasta tres botones al
              pie: tres alturas de texto y una de botones por vehículo, que con cuatro vehículos
              obligaba a desplazar para ver el tercero. Ahora cada tarjeta es UNA fila y la lista
              entra de un vistazo.

              Las acciones se fueron al menú de tres puntos por instrucción del 22-10-2026. Queda
              dicho que eso revierte una decisión del 19-10 que está escrita en el historial: «marcar
              el principal es de un toque y escondido tras Más opciones la gente no lo encuentra».
              Las dos lecturas son defendibles y la elección es de producto; se deja la nota para que
              volver atrás sea un cambio consciente y no un redescubrimiento.
            */
            <Card key={vehicle.id}>
              <div className="lx-vehiculo-fila">
                {/*
                  La imagen: fotografía con licencia si la hay para ESTE vehículo, imagen de
                  prototipo por carrocería y color si no, y el dibujo como última red.

                  `title` y no un rótulo visible: la advertencia de que la imagen es genérica se
                  dice UNA vez arriba de la lista, no cuatro veces dentro de ella. Lo que la tarjeta
                  sí afirma, en palabras y como dato, es el tipo —«Automóvil»— y la descripción.
                  La imagen nunca afirma carrocería: un Hilux declarado automóvil se dibuja como
                  pick-up porque su modelo lo dice, pero la tarjeta sigue diciendo «Automóvil».
                */}
                <VehicleImage
                  vehicleId={vehicle.id}
                  type={vehicle.type}
                  color={vehicle.color}
                  brand={vehicle.brand}
                  model={vehicle.model}
                  year={vehicle.year}
                  relleno
                  className="lx-vehiculo-fila__imagen"
                />

                <div className="lx-vehiculo-fila__datos">
                  <p className="lx-text-amount-lg lx-vehiculo-fila__placa">{vehicle.plate}</p>
                  {/* «Toyota Yaris · Gris · 2015» — el color es una palabra en esta línea y nada
                      más; ninguna parte de la tarjeta se pinta del color del carro. */}
                  <p className="lx-text-meta lx-vehiculo-fila__descripcion">
                    {vehicleDescriptor(vehicle, { includeName: true, colorLabel: colorOf(vehicle.color) })}
                  </p>
                  <div className="lx-vehiculo-fila__insignias">
                    {/* La categoría, en palabras. Una moto y un automóvil se cobran y se
                        fiscalizan distinto, así que esto va junto a la placa y no enterrado en un
                        formulario.

                        Desde el 23-10-2026 dice la CATEGORÍA —«Pick-up», «SUV»— y no sólo
                        «Automóvil». No es un adorno: la tarjeta ya dibujaba la pick-up y al lado
                        escribía «Automóvil», que es una contradicción, no un dato de menos. Sale
                        de la misma resolución que elige la imagen, así que las dos no se pueden
                        separar. Ver `categoriaVisible.ts`. */}
                    <InsigniaDeCategoria
                      vehicle={vehicle}
                      etiquetaDelTipo={typeOf(vehicle.type)}
                      tKey={tKey}
                    />
                    {activeVehicleIds.has(vehicle.id) ? <Badge tone="info">{t('citizen.vehicles.activeSessionBadge')}</Badge> : null}
                    {vehicle.isPrimary ? <Badge tone="success">{t('citizen.vehicles.primaryBadge')}</Badge> : null}
                  </div>
                </div>

                {/* El nombre accesible lleva la placa: con cuatro tarjetas, cuatro botones que se
                    llaman «Más opciones» no le dicen a un lector de pantalla cuál es cuál. */}
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={t('citizen.vehicles.menuForPlate', { plate: vehicle.plate })}
                  className="lx-vehiculo-fila__menu"
                  onClick={() => setMenuVehicle(vehicle)}
                >
                  <IconMore size={20} />
                </Button>
              </div>
            </Card>
          ))}
        </CardStack>
        )}
      </QueryBoundary>

      <Modal
        open={formVehicle !== undefined}
        onClose={closeForm}
        title={formVehicle ? t('citizen.vehicles.form.title.edit') : t('citizen.vehicles.form.title.add')}
        closeLabel={t('common.close')}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {saveError ? <Alert tone="danger">{saveError}</Alert> : null}
          <FormField label={t('citizen.vehicles.form.plateLabel')} hint={t('citizen.vehicles.form.plateHint')} error={plateError}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                invalid={Boolean(plateError)}
                value={form.plate}
                onChange={(e) => setForm((f) => ({ ...f, plate: e.target.value }))}
              />
            )}
          </FormField>
          {/* Both lists are the server's (`/catalog/vehicle-types`, `/catalog/vehicle-colors`),
              translated from the `labelKey` each entry carries. Neither is written down here, so a
              type the platform adds tomorrow appears without a release of this app. */}
          <QueryBoundary query={typeCatalogQuery} errorTitle={t('citizen.vehicles.form.typeLabel')}>
            {(types) => (
              <FormField label={t('citizen.vehicles.form.typeLabel')} hint={t('citizen.vehicles.form.typeHint')}>
                {({ inputId, describedBy }) => (
                  /*
                    `aria-label` además del `<label for>`: el disparador del selector es un
                    `<button>`, así que su nombre accesible sale de su CONTENIDO —«Automóvil»— y no
                    del rótulo. Sin esto, un lector de pantalla anuncia dos campos llamados por su
                    valor actual y ninguno dice de qué campo se trata. Medido con el árbol de
                    accesibilidad el 22-10-2026: los dos comboboxes salían sin nombre.
                  */
                  <Select
                    id={inputId}
                    aria-label={t('citizen.vehicles.form.typeLabel')}
                    aria-describedby={describedBy}
                    value={valorDeSelector(form)}
                    onChange={(value) => setForm((f) => ({ ...f, ...desdeValorDeSelector(value) }))}
                    options={opcionesDeTipo(types)}
                    /*
                      El `<select>` del sistema en el teléfono (22-10-2026, urgente).

                      Con dos opciones la lista propia funcionaba; con ocho, en un iPhone, se abre y
                      no se deja bajar —al arrastrar el dedo se cierra— y «Motocicleta», que es la
                      última, queda inalcanzable. El formulario quedaba inutilizable para registrar
                      una moto. El porqué del fallo está en `Select.tsx`; acá basta saber que en un
                      aparato de dedo manda el control del sistema y en escritorio no cambia nada.
                    */
                    nativoEnMovil
                  />
                )}
              </FormField>
            )}
          </QueryBoundary>
          <QueryBoundary query={colorCatalogQuery} errorTitle={t('citizen.vehicles.form.colorLabel')}>
            {(colors) => (
              <FormField label={t('citizen.vehicles.form.colorLabel')} optionalLabel={t('common.optional')}>
                {({ inputId }) => (
                  <Select
                    id={inputId}
                    aria-label={t('citizen.vehicles.form.colorLabel')}
                    value={form.color}
                    onChange={(value) => setForm((f) => ({ ...f, color: value }))}
                    options={[
                      { value: '', label: t('citizen.vehicles.form.colorUnset') },
                      ...colors.map((entry) => ({ value: entry.value, label: tKey(entry.labelKey) })),
                    ]}
                    /* El color tiene TRECE opciones: el mismo fallo, y peor. Va nativo igual. */
                    nativoEnMovil
                  />
                )}
              </FormField>
            )}
          </QueryBoundary>
          <FormField label={t('citizen.vehicles.form.nameLabel')} optionalLabel={t('common.optional')}>
            {({ inputId }) => (
              <Input id={inputId} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            )}
          </FormField>
          <FormField label={t('citizen.vehicles.form.brandLabel')} optionalLabel={t('common.optional')}>
            {({ inputId }) => (
              <Input id={inputId} value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} />
            )}
          </FormField>
          <FormField label={t('citizen.vehicles.form.modelLabel')} optionalLabel={t('common.optional')}>
            {({ inputId }) => (
              <Input id={inputId} value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} />
            )}
          </FormField>
          <FormField label={t('citizen.vehicles.form.yearLabel')} optionalLabel={t('common.optional')}>
            {({ inputId }) => (
              <Input
                id={inputId}
                type="number"
                inputMode="numeric"
                value={form.year}
                onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
              />
            )}
          </FormField>
          <Checkbox
            label={t('citizen.vehicles.form.isOwnerLabel')}
            checked={form.isOwner}
            onChange={(e) => setForm((f) => ({ ...f, isOwner: e.target.checked }))}
          />
          <Button
            type="button"
            variant="primary"
            fullWidth
            loading={isSaving}
            disabled={!form.plate.trim()}
            onClick={handleSubmit}
            style={{ marginTop: 'var(--lx-space-4)' }}
          >
            {formVehicle ? t('citizen.vehicles.form.submit.edit') : t('citizen.vehicles.form.submit.add')}
          </Button>
        </div>
      </Modal>

      {/*
        Todas las acciones de la tarjeta viven acá desde el 22-10-2026, por instrucción.

        El orden es el de uso: editar es lo que más se hace, marcar principal es ocasional y
        eliminar va al final, separado y en rojo. «Marcar como principal» no aparece cuando ya lo
        es: un botón que no haría nada es peor que ningún botón.
      */}
      <Modal open={menuVehicle !== null} onClose={() => setMenuVehicle(null)} title={menuVehicle?.plate ?? ''} closeLabel={t('common.close')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lx-space-2)' }}>
          {menuVehicle ? (
            <>
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => {
                  const elegido = menuVehicle;
                  setMenuVehicle(null);
                  openEdit(elegido);
                }}
              >
                {t('citizen.vehicles.editCta')}
              </Button>
              {!menuVehicle.isPrimary ? (
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  disabled={setPrimaryVehicle.isPending}
                  onClick={() => {
                    const elegido = menuVehicle;
                    setMenuVehicle(null);
                    void handleSetPrimary(elegido);
                  }}
                >
                  {t('citizen.vehicles.actions.setPrimary')}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="danger"
                fullWidth
                onClick={() => {
                  setDeleteError(null);
                  setDeleteTarget(menuVehicle);
                  setMenuVehicle(null);
                }}
              >
                {t('citizen.vehicles.actions.delete')}
              </Button>
            </>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title={t('citizen.vehicles.delete.confirmTitle')}
        closeLabel={t('common.close')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lx-space-4)' }}>
          {deleteError ? <Alert tone="danger">{deleteError}</Alert> : null}
          <p className="lx-text-body" style={{ margin: 0 }}>
            {t('citizen.vehicles.delete.confirmDescription', { plate: deleteTarget?.plate ?? '' })}
          </p>
          <div className="lx-dialog-actions">
            <Button type="button" variant="secondary" fullWidth onClick={() => setDeleteTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" variant="danger" fullWidth loading={deleteVehicle.isPending} onClick={handleDelete}>
              {t('citizen.vehicles.delete.confirmSubmit')}
            </Button>
          </div>
        </div>
      </Modal>
    </CitizenShell>
  );
}

/**
 * La insignia que dice qué es este vehículo.
 *
 * <h2>Por qué es un componente y no una expresión dentro de la tarjeta</h2>
 *
 * <p>Porque tiene que <b>suscribirse</b> a la carrocería elegida, igual que la imagen. La elección
 * se guarda en el dispositivo y se puede cambiar desde el formulario de esta misma pantalla; si la
 * insignia leyera una vez al montar, guardar «pick-up» cambiaría el dibujo y dejaría la palabra
 * anterior al lado hasta recargar. Es decir: se arreglaría la contradicción del 23-10 y se
 * introduciría la misma contradicción un segundo más tarde. Un hook necesita un componente.</p>
 *
 * <p>Cuando no hay categoría que decir —una moto, un tipo que esta aplicación no conoce— cae en el
 * rótulo del catálogo del servidor, que es lo que la tarjeta mostraba siempre.</p>
 */
function InsigniaDeCategoria({
  vehicle,
  etiquetaDelTipo,
  tKey,
}: {
  vehicle: Vehicle;
  etiquetaDelTipo: string | undefined;
  tKey: (key: string) => string;
}): React.JSX.Element | null {
  const carroceria = useCarroceriaElegida(vehicle.id);
  const categoria = categoriaVisible({
    type: vehicle.type,
    brand: vehicle.brand,
    model: vehicle.model,
    carroceria,
  });
  const texto = categoria ? tKey(categoria.claveDeEtiqueta) : etiquetaDelTipo;
  if (!texto) return null;
  return <Badge tone="neutral">{texto}</Badge>;
}
